import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ModalContext, type ModalContextType, type LoginModalOptions, type CloseWarningConfig, type TutorRegistrationModalOptions } from './ModalContext';
import { pushBreadcrumb } from '@/debug/debugStore';

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalOptions, setLoginModalOptions] = useState<LoginModalOptions>();
  const [showAIMatchModal, setShowAIMatchModal] = useState(false);
  const [showTutorRegistration, setShowTutorRegistration] = useState(false);
  const [tutorRegistrationOptions, setTutorRegistrationOptions] = useState<TutorRegistrationModalOptions>();
  const [showParentRegistration, setShowParentRegistration] = useState(false);
  const [showMusicTutorRegistration, setShowMusicTutorRegistration] = useState(false);
  const [showMusicParentRegistration, setShowMusicParentRegistration] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [closeWarningConfig, setCloseWarningConfig] = useState<CloseWarningConfig>();
  const [onCloseWarningConfirm, setOnCloseWarningConfirm] = useState<(() => void) | null>(null);

  // Manage body scroll lock when any modal is open
  useEffect(() => {
    const isAnyModalOpen = showLoginModal || showAIMatchModal || showTutorRegistration || showParentRegistration || showMusicTutorRegistration || showMusicParentRegistration || showCloseWarning;
    
    if (isAnyModalOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showLoginModal, showAIMatchModal, showTutorRegistration, showParentRegistration, showMusicTutorRegistration, showMusicParentRegistration, showCloseWarning]);

  const value: ModalContextType = {
    // Login Modal
    showLoginModal,
    loginModalOptions,
    openLoginModal: (options?: LoginModalOptions) => {
      setLoginModalOptions(options);
      setShowLoginModal(true);
      pushBreadcrumb({ category: 'user', message: 'Open login modal' });
    },
    closeLoginModal: () => {
      setShowLoginModal(false);
      pushBreadcrumb({ category: 'user', message: 'Close login modal' });
    },

    // AI Match Modal
    showAIMatchModal,
    openAIMatchModal: () => {
      setShowAIMatchModal(true);
      pushBreadcrumb({ category: 'user', message: 'Open AI match modal' });
    },
    closeAIMatchModal: () => {
      setShowAIMatchModal(false);
      pushBreadcrumb({ category: 'user', message: 'Close AI match modal' });
    },

    // Tutor Registration Modal
    showTutorRegistration,
    tutorRegistrationOptions,
    openTutorRegistration: (options?: TutorRegistrationModalOptions) => {
      setTutorRegistrationOptions(options);
      setShowTutorRegistration(true);
      pushBreadcrumb({ category: 'user', message: 'Open tutor registration' });
    },
    closeTutorRegistration: () => {
      setShowTutorRegistration(false);
      setTutorRegistrationOptions(undefined);
      pushBreadcrumb({ category: 'user', message: 'Close tutor registration' });
    },

    // Parent Registration Modal
    showParentRegistration,
    openParentRegistration: () => {
      setShowParentRegistration(true);
      pushBreadcrumb({ category: 'user', message: 'Open parent registration' });
    },
    closeParentRegistration: () => {
      setShowParentRegistration(false);
      pushBreadcrumb({ category: 'user', message: 'Close parent registration' });
    },

    showMusicTutorRegistration,
    openMusicTutorRegistration: () => {
      setShowMusicTutorRegistration(true);
      pushBreadcrumb({ category: 'user', message: 'Open music tutor registration' });
    },
    closeMusicTutorRegistration: () => setShowMusicTutorRegistration(false),
    showMusicParentRegistration,
    openMusicParentRegistration: () => {
      setShowMusicParentRegistration(true);
      pushBreadcrumb({ category: 'user', message: 'Open music parent registration' });
    },
    closeMusicParentRegistration: () => setShowMusicParentRegistration(false),

    // Close Warning Modal
    showCloseWarning,
    closeWarningConfig,
    openCloseWarning: (config?: CloseWarningConfig) => {
      setCloseWarningConfig(config);
      setShowCloseWarning(true);
    },
    closeCloseWarning: () => setShowCloseWarning(false),
    onCloseWarningConfirm,
    setOnCloseWarningConfirm,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      
      {/* Close Warning Modal - Rendered at App Level with higher z-index to appear above LoginModal */}
      {showCloseWarning && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
          {/* Background overlay */}
          <div
            className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setShowCloseWarning(false)}
          />
          
          {/* Warning Modal Container */}
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md z-10">
            {/* Header */}
            <div className="bg-primary px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">
                {closeWarningConfig?.title || 'Incomplete Profile'}
              </h3>
              <button
                onClick={() => setShowCloseWarning(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <p className="text-gray-700 mb-6">
                {closeWarningConfig?.message || 'If you exit without completing your profile, you will be logged out. Do you want to continue?'}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowCloseWarning(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {closeWarningConfig?.cancelText || 'Continue Editing'}
                </button>
                <button
                  onClick={() => {
                    setShowCloseWarning(false);
                    if (closeWarningConfig?.onConfirm) {
                      closeWarningConfig.onConfirm();
                    } else if (onCloseWarningConfirm) {
                      onCloseWarningConfirm();
                    }
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                >
                  {closeWarningConfig?.confirmText || 'Logout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
};
